import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useState } from 'react'
import { useRLSDebug } from '@/hooks/useRLSDebug'
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Copy, ChevronDown, ChevronRight } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface PermissionResult {
  table_name: string
  operation: string
  allowed: boolean
  error_message: string | null
  details: any
}

interface TestContext {
  auth_user_id: string
  computed_account_id: string
  testing_account_id: string
  testing_account_name: string
  is_testing_different_account: boolean
  is_admin: boolean
}

export default function Debug() {
  const { user } = useAuth()
  const { debugInfo, loading, error, lastUpdated, refresh } = useRLSDebug()
  const [copied, setCopied] = useState(false)
  const [permissions, setPermissions] = useState<PermissionResult[]>([])
  const [loadingPermissions, setLoadingPermissions] = useState(false)
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())
  const [testContext, setTestContext] = useState<TestContext | null>(null)

  const sessionData = localStorage.getItem('rolt_user_session')
  const parsedSession = sessionData ? JSON.parse(sessionData) : null

  const allLocalStorageData = {
    rolt_user_session: parsedSession,
    supabase_auth: localStorage.getItem('sb-' + import.meta.env.VITE_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token'),
  }

  const handleCopyToClipboard = () => {
    const debugData = {
      rls_debug: debugInfo,
      permissions,
      user,
      session: parsedSession,
      localStorage: allLocalStorageData,
      timestamp: new Date().toISOString()
    }

    navigator.clipboard.writeText(JSON.stringify(debugData, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const loadPermissions = async () => {
    if (!supabase) return

    setLoadingPermissions(true)
    try {
      const currentAccountId = user?.account_id || parsedSession?.account_id || null

      const { data, error } = await supabase.rpc('debug_test_all_permissions', {
        p_test_account_id: currentAccountId
      })

      if (error) throw error

      const systemInfo = (data || []).find((row: PermissionResult) => row.operation === 'INFO')
      if (systemInfo?.details) {
        setTestContext(systemInfo.details as TestContext)
      }

      const permissionsOnly = (data || []).filter((row: PermissionResult) => row.operation !== 'INFO')
      setPermissions(permissionsOnly)
    } catch (err) {
      console.error('Error loading permissions:', err)
    } finally {
      setLoadingPermissions(false)
    }
  }

  const toggleTable = (tableName: string) => {
    const newExpanded = new Set(expandedTables)
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName)
    } else {
      newExpanded.add(tableName)
    }
    setExpandedTables(newExpanded)
  }

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (perm.operation === 'INFO') return acc
    if (!acc[perm.table_name]) {
      acc[perm.table_name] = []
    }
    acc[perm.table_name].push(perm)
    return acc
  }, {} as Record<string, PermissionResult[]>)

  const StatusBadge = ({ isOk, label }: { isOk: boolean; label: string }) => {
    return (
      <Badge
        variant={isOk ? 'default' : 'destructive'}
        className={isOk ? 'bg-green-600' : 'bg-red-600'}
      >
        {isOk ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {label}
      </Badge>
    )
  }

  const UUIDDisplay = ({ value }: { value: string | null }) => {
    if (!value) {
      return <span className="text-red-400 font-mono text-sm">NULL</span>
    }
    return <span className="text-green-400 font-mono text-sm">{value}</span>
  }

  const renderObject = (obj: any, level = 0) => {
    if (!obj) return <span className="text-red-400">null</span>

    if (typeof obj !== 'object') {
      return <span className="text-green-400">{String(obj)}</span>
    }

    return (
      <div className="font-mono text-sm">
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} style={{ marginLeft: `${level * 16}px` }}>
            <span className="text-blue-400">{key}:</span>{' '}
            {typeof value === 'object' && value !== null ? (
              <div className="ml-4">{renderObject(value, level + 1)}</div>
            ) : (
              <span className="text-green-400">{String(value)}</span>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="w-full p-6 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Debug Panel</h1>
          <p className="text-sm text-neutral-400 mt-2">
            Ferramenta secreta de diagnóstico e simulação de permissões RLS
          </p>
          {lastUpdated && (
            <p className="text-sm text-neutral-400 mt-1">
              Última atualização: {lastUpdated.toLocaleTimeString('pt-BR')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={refresh}
            disabled={loading}
            className="bg-neutral-700 hover:bg-neutral-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button
            onClick={handleCopyToClipboard}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Copy className="w-4 h-4 mr-2" />
            {copied ? 'Copiado!' : 'Copiar JSON'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="bg-red-900/20 border-red-700 p-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-xl font-semibold text-red-500">Erro ao carregar informações RLS</h3>
          </div>
          <p className="text-red-400 mt-2">{error}</p>
        </Card>
      )}

      {debugInfo && (
        <>
          <Card className="bg-neutral-800 border-neutral-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Identidade do Usuário (Auth)</h2>
              <StatusBadge isOk={debugInfo.auth.is_authenticated} label={debugInfo.auth.is_authenticated ? 'Autenticado' : 'Não Autenticado'} />
            </div>
            <div className="bg-neutral-900 p-4 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-neutral-400">auth.uid():</span>
                <UUIDDisplay value={debugInfo.auth.uid} />
              </div>
            </div>
          </Card>

          <Card className="bg-neutral-800 border-neutral-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Funções RLS (Row Level Security)</h2>
            <div className="bg-neutral-900 p-4 rounded-lg space-y-4">
              <div className="border-b border-neutral-700 pb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-semibold">get_user_agency_id()</span>
                  <StatusBadge
                    isOk={!debugInfo.rls_functions.get_user_agency_id.is_null}
                    label={debugInfo.rls_functions.get_user_agency_id.is_null ? 'NULL' : 'OK'}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-400 text-sm">Retorno:</span>
                  <UUIDDisplay value={debugInfo.rls_functions.get_user_agency_id.result} />
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Busca agency_id do usuário em agency_users. Usado para validar INSERT em accounts.
                </p>
              </div>

              <div className="border-b border-neutral-700 pb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-semibold">get_auth_account_id()</span>
                  <StatusBadge
                    isOk={!debugInfo.rls_functions.get_auth_account_id.is_null}
                    label={debugInfo.rls_functions.get_auth_account_id.is_null ? 'NULL' : 'OK'}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-400 text-sm">Retorno:</span>
                  <UUIDDisplay value={debugInfo.rls_functions.get_auth_account_id.result} />
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Busca account_id do usuário. Prioridade: account_users → agency_user_sessions.
                </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-semibold">get_jwt_account_id()</span>
                  <StatusBadge
                    isOk={!debugInfo.rls_functions.get_jwt_account_id.is_null}
                    label={debugInfo.rls_functions.get_jwt_account_id.is_null ? 'NULL' : 'OK'}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-400 text-sm">Retorno:</span>
                  <UUIDDisplay value={debugInfo.rls_functions.get_jwt_account_id.result} />
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Extrai account_id dos claims do JWT. Usado por account_users.
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-neutral-800 border-neutral-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Registros nas Tabelas</h2>
            <div className="space-y-4">
              <div className="bg-neutral-900 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white font-semibold">Tabela: agency_users</span>
                  <StatusBadge
                    isOk={debugInfo.user_records.agency_users.exists}
                    label={debugInfo.user_records.agency_users.exists ? 'Existe' : 'Não existe'}
                  />
                </div>
                {debugInfo.user_records.agency_users.exists && debugInfo.user_records.agency_users.data && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">ID:</span>
                      <span className="text-white font-mono text-xs">{debugInfo.user_records.agency_users.data.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">auth_user_id:</span>
                      <span className="text-white font-mono text-xs">{debugInfo.user_records.agency_users.data.auth_user_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">agency_id:</span>
                      <span className="text-green-400 font-mono text-xs">{debugInfo.user_records.agency_users.data.agency_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Email:</span>
                      <span className="text-white">{debugInfo.user_records.agency_users.data.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Nome:</span>
                      <span className="text-white">{debugInfo.user_records.agency_users.data.name}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-neutral-900 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white font-semibold">Tabela: account_users</span>
                  <StatusBadge
                    isOk={debugInfo.user_records.account_users.exists}
                    label={debugInfo.user_records.account_users.exists ? 'Existe' : 'Não existe'}
                  />
                </div>
                {debugInfo.user_records.account_users.exists && debugInfo.user_records.account_users.data && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">ID:</span>
                      <span className="text-white font-mono text-xs">{debugInfo.user_records.account_users.data.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">auth_user_id:</span>
                      <span className="text-white font-mono text-xs">{debugInfo.user_records.account_users.data.auth_user_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">account_id:</span>
                      <span className="text-green-400 font-mono text-xs">{debugInfo.user_records.account_users.data.account_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Email:</span>
                      <span className="text-white">{debugInfo.user_records.account_users.data.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Profile:</span>
                      <span className="text-white">{debugInfo.user_records.account_users.data.profile}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-neutral-900 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white font-semibold">Tabela: agency_user_sessions</span>
                  <StatusBadge
                    isOk={debugInfo.session.has_active_session}
                    label={debugInfo.session.has_active_session ? 'Sessão Ativa' : 'Sem sessão'}
                  />
                </div>
                {debugInfo.session.has_active_session && debugInfo.session.data && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">account_id:</span>
                      <span className="text-green-400 font-mono text-xs">{debugInfo.session.data.account_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">agency_id:</span>
                      <span className="text-green-400 font-mono text-xs">{debugInfo.session.data.agency_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Expira em:</span>
                      <span className="text-white">{new Date(debugInfo.session.data.expires_at).toLocaleString('pt-BR')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="bg-neutral-800 border-neutral-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Simulação de Policies - Todas as Tabelas</h2>
              <Button
                onClick={loadPermissions}
                disabled={loadingPermissions}
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingPermissions ? 'animate-spin' : ''}`} />
                {permissions.length > 0 ? 'Atualizar' : 'Carregar'}
              </Button>
            </div>

            {testContext && (
              <div className="mb-6 bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                  <h3 className="text-blue-400 font-semibold">Contexto do Teste</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-neutral-400">Conta Testada:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-white font-semibold">{testContext.testing_account_name || 'N/A'}</span>
                      {testContext.is_admin && (
                        <Badge className="bg-purple-600 text-xs">ADMIN</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-neutral-400">Account ID:</span>
                    <UUIDDisplay value={testContext.testing_account_id} />
                  </div>
                  <div>
                    <span className="text-neutral-400">Computed Account ID:</span>
                    <UUIDDisplay value={testContext.computed_account_id} />
                  </div>
                  <div>
                    <span className="text-neutral-400">Auth User ID:</span>
                    <UUIDDisplay value={testContext.auth_user_id} />
                  </div>
                </div>
                {testContext.is_testing_different_account && (
                  <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-700 rounded text-yellow-400 text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>Testando conta diferente da conta atual</span>
                  </div>
                )}
              </div>
            )}

            {loadingPermissions && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                <span className="text-white ml-3">Testando permissões em todas as tabelas...</span>
              </div>
            )}

            {!loadingPermissions && permissions.length === 0 && (
              <div className="bg-neutral-900 p-8 rounded-lg text-center">
                <AlertCircle className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-400">Clique em "Carregar" para testar permissões em todas as tabelas</p>
              </div>
            )}

            {!loadingPermissions && permissions.length > 0 && (
              <>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-900/20 border border-green-700 p-4 rounded-lg">
                    <p className="text-green-400 text-2xl font-bold">
                      {Object.values(groupedPermissions).filter(perms => perms.find(p => p.operation === 'SELECT')?.allowed).length}
                    </p>
                    <p className="text-neutral-400 text-sm">SELECT permitido</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-700 p-4 rounded-lg">
                    <p className="text-blue-400 text-2xl font-bold">
                      {Object.values(groupedPermissions).filter(perms => perms.find(p => p.operation === 'INSERT')?.allowed).length}
                    </p>
                    <p className="text-neutral-400 text-sm">INSERT permitido</p>
                  </div>
                  <div className="bg-yellow-900/20 border border-yellow-700 p-4 rounded-lg">
                    <p className="text-yellow-400 text-2xl font-bold">
                      {Object.values(groupedPermissions).filter(perms => perms.find(p => p.operation === 'UPDATE')?.allowed).length}
                    </p>
                    <p className="text-neutral-400 text-sm">UPDATE permitido</p>
                  </div>
                  <div className="bg-red-900/20 border border-red-700 p-4 rounded-lg">
                    <p className="text-red-400 text-2xl font-bold">
                      {Object.values(groupedPermissions).filter(perms => perms.find(p => p.operation === 'DELETE')?.allowed).length}
                    </p>
                    <p className="text-neutral-400 text-sm">DELETE permitido</p>
                  </div>
                </div>

                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {Object.entries(groupedPermissions).map(([tableName, perms]) => {
                    const selectPerm = perms.find(p => p.operation === 'SELECT')
                    const insertPerm = perms.find(p => p.operation === 'INSERT')
                    const updatePerm = perms.find(p => p.operation === 'UPDATE')
                    const deletePerm = perms.find(p => p.operation === 'DELETE')
                    const isExpanded = expandedTables.has(tableName)

                    return (
                      <div key={tableName} className="bg-neutral-900 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleTable(tableName)}
                          className="w-full p-4 flex items-center justify-between hover:bg-neutral-800 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-neutral-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-neutral-400" />
                            )}
                            <span className="text-white font-semibold">{tableName}</span>
                          </div>
                          <div className="flex gap-2">
                            <Badge className={selectPerm?.allowed ? 'bg-green-600' : 'bg-red-600'}>
                              <CheckCircle2 className={`w-3 h-3 mr-1 ${!selectPerm?.allowed && 'hidden'}`} />
                              <XCircle className={`w-3 h-3 mr-1 ${selectPerm?.allowed && 'hidden'}`} />
                              SELECT
                            </Badge>
                            <Badge className={insertPerm?.allowed ? 'bg-green-600' : 'bg-neutral-600'}>
                              INSERT
                            </Badge>
                            <Badge className={updatePerm?.allowed ? 'bg-green-600' : 'bg-neutral-600'}>
                              UPDATE
                            </Badge>
                            <Badge className={deletePerm?.allowed ? 'bg-green-600' : 'bg-neutral-600'}>
                              DELETE
                            </Badge>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-4 space-y-3 border-t border-neutral-800">
                            {selectPerm && (
                              <div className="flex items-start justify-between pt-3">
                                <div className="flex items-center gap-2">
                                  {selectPerm.allowed ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  )}
                                  <span className="text-white font-mono text-sm">SELECT</span>
                                </div>
                                <div className="text-right">
                                  {selectPerm.allowed ? (
                                    <div>
                                      <p className="text-green-400 text-sm">
                                        Permitido
                                      </p>
                                      {selectPerm.details?.count !== undefined && (
                                        <p className="text-neutral-400 text-xs">
                                          {selectPerm.details.count} registros visíveis
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-red-400 text-sm">{selectPerm.error_message || 'Bloqueado'}</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {insertPerm && (
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  {insertPerm.allowed ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-neutral-500" />
                                  )}
                                  <span className="text-white font-mono text-sm">INSERT</span>
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm ${insertPerm.allowed ? 'text-green-400' : 'text-neutral-400'}`}>
                                    {insertPerm.details?.note || 'Sem permissão'}
                                  </p>
                                </div>
                              </div>
                            )}

                            {updatePerm && (
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  {updatePerm.allowed ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-neutral-500" />
                                  )}
                                  <span className="text-white font-mono text-sm">UPDATE</span>
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm ${updatePerm.allowed ? 'text-green-400' : 'text-neutral-400'}`}>
                                    {updatePerm.details?.has_rows ? 'Tem registros atualizáveis' : 'Sem registros'}
                                  </p>
                                </div>
                              </div>
                            )}

                            {deletePerm && (
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  {deletePerm.allowed ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-neutral-500" />
                                  )}
                                  <span className="text-white font-mono text-sm">DELETE</span>
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm ${deletePerm.allowed ? 'text-green-400' : 'text-neutral-400'}`}>
                                    {deletePerm.details?.has_rows ? 'Tem registros deletáveis' : 'Sem registros'}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </Card>
        </>
      )}

      {loading && !debugInfo && (
        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <div className="flex items-center justify-center gap-3">
            <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
            <span className="text-white">Carregando informações de RLS...</span>
          </div>
        </Card>
      )}

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
          Usuário Atual (AuthContext)
        </h2>
        <div className="bg-neutral-900 p-4 rounded-lg overflow-auto max-h-[400px]">
          <pre className="text-white text-sm">
            {user ? renderObject(user) : <span className="text-red-400">Nenhum usuário logado</span>}
          </pre>
        </div>
      </Card>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
          Sessão (localStorage - rolt_user_session)
        </h2>
        <div className="bg-neutral-900 p-4 rounded-lg overflow-auto max-h-[400px]">
          <pre className="text-white text-sm">
            {parsedSession ? renderObject(parsedSession) : <span className="text-red-400">Nenhuma sessão encontrada</span>}
          </pre>
        </div>
      </Card>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
          Informações da Sessão
        </h2>
        <div className="bg-neutral-900 p-4 rounded-lg space-y-2">
          {parsedSession && (
            <>
              <div className="flex justify-between">
                <span className="text-neutral-400">Criado em:</span>
                <span className="text-white">
                  {new Date(parsedSession.createdAt).toLocaleString('pt-BR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Expira em:</span>
                <span className="text-white">
                  {new Date(parsedSession.expiresAt).toLocaleString('pt-BR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Agency ID:</span>
                <span className="text-white">{parsedSession.agencyId || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Token:</span>
                <span className="text-white font-mono text-xs truncate max-w-[300px]">
                  {parsedSession.token?.substring(0, 30)}...
                </span>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
          Usuário na Sessão
        </h2>
        <div className="bg-neutral-900 p-4 rounded-lg space-y-2">
          {parsedSession?.user && (
            <>
              <div className="flex justify-between">
                <span className="text-neutral-400">ID:</span>
                <span className="text-white">{parsedSession.user.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Nome:</span>
                <span className="text-white">{parsedSession.user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Email:</span>
                <span className="text-white">{parsedSession.user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Account ID:</span>
                <span className="text-white">{parsedSession.user.account_id || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Auth User ID:</span>
                <span className="text-white">{parsedSession.user.auth_user_id || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Profile:</span>
                <span className="text-white">{parsedSession.user.profile || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Type:</span>
                <span className="text-white">{parsedSession.user.type || 'N/A'}</span>
              </div>
            </>
          )}
        </div>
      </Card>

      {parsedSession?.user?.account && (
        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">
            Conta Atual (Account)
          </h2>
          <div className="bg-neutral-900 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-neutral-400">ID:</span>
              <span className="text-white">{parsedSession.user.account.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-400">Nome:</span>
              <span className="text-white">{parsedSession.user.account.nome}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-400">Agency ID:</span>
              <span className="text-white">{parsedSession.user.account.agency_id || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-400">Instância:</span>
              <span className="text-white">{parsedSession.user.account.instance || 'N/A'}</span>
            </div>
          </div>
        </Card>
      )}

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
          Todos os Dados (localStorage)
        </h2>
        <div className="bg-neutral-900 p-4 rounded-lg overflow-auto max-h-[400px]">
          <pre className="text-white text-sm">
            {renderObject(allLocalStorageData)}
          </pre>
        </div>
      </Card>
    </div>
  )
}
